import { Action } from "@packages/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	getRecurringTemplateQueryOptions,
	useDeleteRecurringTemplate,
	useUpdateRecurringTemplate,
} from "@/api/financial";
import { PageHeader } from "@/components/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
	RecurringTemplateForm,
	type RecurringTemplateFormValues,
} from "../-components/RecurringTemplateForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/recurring/$templateId/",
)({
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getRecurringTemplateQueryOptions(params.orgId, params.templateId),
		);
	},
	component: RecurringTemplateDetailPage,
});

function RecurringTemplateDetailPage() {
	const { orgId, templateId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const { formatForDateInput } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [isDeleteOpen, setIsDeleteOpen] = useState(false);

	const { data: template } = useSuspenseQuery(
		getRecurringTemplateQueryOptions(orgId, templateId),
	);

	const updateMutation = useUpdateRecurringTemplate();
	const deleteMutation = useDeleteRecurringTemplate();

	const goToList = () =>
		navigate({ to: "/app/org/$orgId/financial/recurring", params: { orgId } });

	const defaults: Partial<RecurringTemplateFormValues> = {
		description: template.description,
		party: template.party ?? "",
		direction: template.direction,
		amountDefault: template.amountDefault ?? "",
		categoryId: template.categoryId,
		establishmentId: template.establishmentId ?? undefined,
		frequency: template.frequency,
		customIntervalMonths:
			template.customIntervalMonths != null
				? String(template.customIntervalMonths)
				: "",
		dueDayOfMonth:
			template.dueDayOfMonth != null ? String(template.dueDayOfMonth) : "",
		startDate: formatForDateInput(new Date(template.startDate).getTime()),
		endDate: template.endDate
			? formatForDateInput(new Date(template.endDate).getTime())
			: "",
		autoPost: template.autoPost,
		isActive: template.isActive,
	};

	return (
		<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
			<PageHeader title={template.description} showBackButton />

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					<RecurringTemplateForm
						orgId={orgId}
						defaultValues={defaults}
						submitLabel={t("common.save")}
						isSubmitting={updateMutation.isPending}
						onSubmit={(data) =>
							updateMutation
								.mutateAsync({ organizationId: orgId, templateId, data })
								.then(() => goToList())
						}
						onCancel={goToList}
					/>
				</div>
			</div>

			{canManage && (
				<div className="flex justify-end">
					<Button
						variant="outline"
						className="text-error"
						onClick={() => setIsDeleteOpen(true)}
						disabled={deleteMutation.isPending}
					>
						<Trash2 className="mr-2 h-4 w-4" />
						{t("financial.recurring.delete.action")}
					</Button>
				</div>
			)}

			<AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.recurring.delete.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.recurring.delete.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								deleteMutation.mutate(
									{ organizationId: orgId, templateId },
									{ onSuccess: () => goToList() },
								)
							}
							disabled={deleteMutation.isPending}
						>
							{deleteMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.recurring.delete.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
